import { ChevronDown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { CRS, CRSMappings, CRSValues, State, States } from "./models";
import { PredictiveSuburbManager } from "./predictive-suburb-manager";

export const DemoControls = (predictiveManager: PredictiveSuburbManager) => {
  const toggleState = (state: State) => {
    if (predictiveManager.stateFilter.includes(state)) {
      predictiveManager.setStateFilter(
        predictiveManager.stateFilter.filter((s) => s !== state),
      );
    } else {
      predictiveManager.setStateFilter([
        ...predictiveManager.stateFilter,
        state,
      ]);
    }
  };

  return (
    <Card>
      <CardContent className="py-4">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">
                Maximum Number of Results:{" "}
                <span className="font-semibold">
                  {predictiveManager.maxResults}
                </span>
              </Label>
              <Slider
                value={[predictiveManager.maxResults]}
                min={1}
                max={20}
                step={1}
                onValueChange={([v]) => predictiveManager.setMaxResults(v)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">
                Client De-bounce:{" "}
                <span className="font-semibold">
                  {predictiveManager.debounce}ms
                </span>
              </Label>
              <Slider
                value={[predictiveManager.debounce]}
                min={100}
                max={1000}
                step={100}
                onValueChange={([v]) => predictiveManager.setDebounce(v)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">State Filter</Label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    "border-input bg-input/20 dark:bg-input/30 hover:bg-input/40 dark:hover:bg-input/50 focus-visible:border-ring focus-visible:ring-ring/30 h-7 rounded-md border px-2 py-0.5 text-sm flex w-full items-center justify-between gap-2 outline-none focus-visible:ring-2",
                  )}
                >
                  <span className="truncate text-left">
                    {predictiveManager.stateFilter.length > 0 ? (
                      predictiveManager.stateFilter.join(", ")
                    ) : (
                      <span className="text-muted-foreground">Select...</span>
                    )}
                  </span>
                  <ChevronDown className="text-muted-foreground size-4 shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-(--radix-popover-trigger-width) p-2"
              >
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {States.map((state) => (
                    <div key={state} className="flex items-center gap-2">
                      <Checkbox
                        id={`state-${state}`}
                        checked={predictiveManager.stateFilter.includes(state)}
                        onCheckedChange={() => toggleState(state)}
                      />
                      <Label
                        htmlFor={`state-${state}`}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {state}
                      </Label>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="postcode-filter" className="text-xs">
              Postcode
            </Label>
            <Input
              id="postcode-filter"
              value={predictiveManager.postcode}
              onChange={(e) => predictiveManager.setPostcode(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">CRS</Label>
            <Select
              value={predictiveManager.crs}
              onValueChange={(v) => predictiveManager.setCrs(v as CRS)}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CRSValues.map((crs) => (
                  <SelectItem key={crs} value={crs}>
                    {CRSMappings.get(crs)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
